/**
 * Taking the money.
 *
 * Handles the three things this shop does every day: cash in shekels, a card on
 * the bank's own terminal, and tourists paying in dollars, dinars or euros. Any
 * of them can be split across one sale.
 *
 * The maths is the shared settle() function - the same one the server re-runs
 * when the sale arrives, so the till and the books can never disagree.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  dueInCurrency,
  newId,
  NON_CASH_METHODS,
  settle,
  type Currency,
  type PaymentInput,
  type PaymentMethod,
} from '@pos/shared';
import { useT } from '../../core/i18n/index.js';
import { useSettings } from '../../core/state/settings.js';
import { useSync } from '../../core/sync.js';
import { api, ApiError } from '../../core/api.js';
import { Button, Field, Modal, MoneyInput } from '../../core/ui/ui.js';
import { paymentLabel } from '../../core/format.js';

export interface TakenPayment extends PaymentInput {
  creditCode?: string;
}

export function PaymentDialog({
  total,
  onDone,
  onCancel,
  allowCredit = true,
  allowGift = true,
  allowTab = false,
  customer = null,
  onNeedCustomer,
}: {
  /** What is owed, in agorot, before cash rounding. */
  total: number;
  onDone: (payments: TakenPayment[], change: number, rounding: number) => void;
  onCancel: () => void;
  allowCredit?: boolean;
  allowGift?: boolean;
  /** Whether this shop lets a customer walk out owing money. */
  allowTab?: boolean;
  /** Who the debt would go to. A debt with no name is not a debt. */
  customer?: { id: string; name: string } | null;
  /** Ask the till to put a name on the sale, then come back here. */
  onNeedCustomer?: () => void;
}) {
  const t = useT();
  const { settings, currencies, money, amount } = useSettings();
  const { state } = useSync();

  const active = useMemo(() => currencies.filter((c) => c.active), [currencies]);
  const ils = active.find((c) => c.code === 'ILS') ?? active[0];

  const [taken, setTaken] = useState<TakenPayment[]>([]);
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [currency, setCurrency] = useState<Currency>(ils);
  const [entry, setEntry] = useState(0);
  const [creditCode, setCreditCode] = useState('');
  const [creditInfo, setCreditInfo] = useState<{ remaining: number; usable: boolean } | null>(null);
  const [error, setError] = useState('');
  /* Something worth saying that is not a refusal - a card that paid what it
     could, for instance. Shown plainly, not in red: nothing went wrong. */
  const [note, setNote] = useState('');
  /*
   * Nearly every sale is the whole bill, in one go, in cash or on a card. That
   * is the whole of the first screen. Split payments, tourists paying in
   * dollars and store credit are all still here, one tap further in.
   */
  const [detail, setDetail] = useState(false);

  // What is left to pay, with the cash rounding rule applied as it stands.
  const current = useMemo(
    () => settle(total, taken, { cashStepAgorot: settings.cashStepAgorot }),
    [total, taken, settings.cashStepAgorot],
  );

  const remainingIls = current.remaining;

  // Pre-fill the box with exactly what is still owed, in the chosen currency.
  useEffect(() => {
    if (method === 'card' || method === 'tab') setEntry(remainingIls);
    else if (currency.code === 'ILS') {
      setEntry(remainingIls > 0 ? remainingIls + (taken.length === 0 ? current.rounding : 0) : 0);
    } else {
      setEntry(remainingIls > 0 ? dueInCurrency(remainingIls, currency) : 0);
    }
  }, [method, currency, remainingIls, taken.length, current.rounding]);

  /*
   * A store credit and a gift card are the same balance under a code, and the
   * button says which one the customer is holding. They are looked up down
   * different paths on purpose: asking for a gift card and being handed a store
   * credit is a mistake worth catching at the counter, not at the end of the day
   * when the gift cards sold no longer match the gift cards spent.
   */
  const isVoucher = method === 'credit' || method === 'gift';
  type VoucherInfo = { remaining: number; usable: boolean };
  /** Returns what the code is worth, and puts it on screen. Null means unusable. */
  const lookupCredit = async (code: string): Promise<VoucherInfo | null> => {
    setError('');
    const wanted = code.trim().toUpperCase();
    /*
     * Nothing typed, nothing to look up.
     *
     * Asking for an empty code used to fetch /exchange/giftcards/ with the code
     * missing, which is the LIST of every card - an answer with no balance on
     * it. That was then shown as the balance, and formatting a missing number
     * threw inside the render, so the whole payment dialog came down and took
     * the sale in progress with it. Pressing Search on an empty box is a normal
     * thing to do; it must do nothing at all.
     */
    if (!wanted) {
      setCreditInfo(null);
      return null;
    }
    try {
      const path = method === 'gift' ? 'giftcards' : 'credits';
      const c = await api<{ remaining: number; usable: boolean }>(`/exchange/${path}/${encodeURIComponent(wanted)}`);
      // Only an answer carrying a real balance is a voucher. Anything else is
      // treated as not found rather than believed and put on the screen.
      if (!Number.isFinite(c?.remaining)) {
        setCreditInfo(null);
        setError(t(method === 'gift' ? 'pay.giftNotFound' : 'pay.creditNotFound'));
        return null;
      }
      setCreditInfo(c);
      if (!c.usable) {
        setError(t(method === 'gift' ? 'pay.giftNotEnough' : 'pay.creditNotEnough'));
        return null;
      }
      // Never more than the card holds, and never more than is owed.
      setEntry(Math.min(c.remaining, remainingIls));
      return c;
    } catch (e) {
      setCreditInfo(null);
      // The server says WHY - the code is a store credit, not a gift card; the
      // card has expired - and that is worth more at the counter than "not found".
      setError(
        e instanceof ApiError && e.status !== 0 && e.message
          ? e.message
          : t(method === 'gift' ? 'pay.giftNotFound' : 'pay.creditNotFound'),
      );
      return null;
    }
  };

  const add = async () => {
    setError('');
    setNote('');
    let voucher: VoucherInfo | null = null;
    if (entry <= 0) return;

    if (method === 'tab' && !customer) {
      setError(t('pay.tabNeedsCustomer'));
      return;
    }

    if (isVoucher) {
      if (state === 'offline') {
        setError(t(method === 'gift' ? 'pay.giftNeedsOnline' : 'pay.creditNeedsOnline'));
        return;
      }
      /*
       * Typing a code and pressing Add is the natural thing to do, and pressing
       * Add is also what blurs the box - so the lookup was only just starting
       * when this ran, creditInfo was still empty, and the till answered "that
       * card does not have enough on it" about a card it had never asked about.
       * Look it up here and wait for the answer.
       */
      voucher = creditInfo;
      if (!voucher && creditCode.trim()) voucher = await lookupCredit(creditCode);
      if (!voucher?.usable) {
        // lookupCredit has already said why; do not talk over it.
        if (!error) setError(t(method === 'gift' ? 'pay.giftNotFound' : 'pay.creditNotFound'));
        return;
      }
    }

    /*
     * A card worth less than the bill pays what it can.
     *
     * Asking for more than the card holds has exactly one sensible answer -
     * take the balance and collect the rest - so it does that, and says so,
     * instead of refusing and leaving the cashier to work out the number. The
     * shop cannot lose by it: the card's balance is the ceiling, the till never
     * gives change from a voucher, and the server checks the balance again
     * before a single agora comes off it.
     */
    let amount = entry;
    if (isVoucher && voucher) {
      /*
       * Two ceilings, and a voucher is held under both: it can never pay more
       * than it is worth, and it can never pay more than is owed. The second
       * matters as much as the first - the difference would come back as cash,
       * turning a card that was bought with money into money again, out of the
       * drawer, against nothing sold.
       */
      const mostFromCard = Math.min(voucher.remaining, remainingIls);
      if (entry > mostFromCard) {
        amount = mostFromCard;
        setNote(
          entry > voucher.remaining
            ? t(method === 'gift' ? 'pay.giftOnlyHas' : 'pay.creditOnlyHas', { amount: money(amount) })
            : t('pay.noChangeFrom', { method: paymentLabel(method, t) }),
        );
      }
    }

    /*
     * Only cash can be handed over in excess, because only cash gets change.
     *
     * Take 200 off a gift card against a 50 bill and the till would hand back
     * 150 in notes - a card bought with money turned into money again, out of
     * the drawer, against nothing sold. The same is true of a card terminal and
     * of the slate. Cash is the exception: change is the whole point of it.
     */
    if (NON_CASH_METHODS.includes(method) && !isVoucher && entry > remainingIls) {
      setError(t('pay.noChangeFrom', { method: paymentLabel(method, t) }));
      return;
    }

    setTaken((list) => [
      ...list,
      {
        id: newId(),
        method,
        currency: method === 'cash' ? currency.code : 'ILS',
        amount,
        rateMicros: method === 'cash' ? currency.rateMicros : 1_000_000,
        decimals: method === 'cash' ? currency.decimals : 2,
        reference: isVoucher
          ? creditCode.trim().toUpperCase()
          : method === 'tab'
            ? customer?.name
            : undefined,
      },
    ]);
    setEntry(0);
    setCreditCode('');
    setCreditInfo(null);
  };

  /** One tap for the common case: the whole bill, in one method. */
  const payAll = () => {
    /*
     * Never a voucher. This writes the payment directly, without the card
     * number and without checking the balance, so a gift card taken this way
     * would settle the bill while nothing came off any card.
     */
    if (isVoucher) return;
    const st = settle(total, [{ id: 'x', method, currency: 'ILS', amount: 0, rateMicros: 1_000_000, decimals: 2 }], {
      cashStepAgorot: settings.cashStepAgorot,
    });
    const due = method === 'cash' ? st.payable : total;
    const amt = method === 'cash' && currency.code !== 'ILS' ? dueInCurrency(due, currency) : due;
    setTaken([
      {
        id: newId(),
        method,
        currency: method === 'cash' ? currency.code : 'ILS',
        amount: amt,
        rateMicros: method === 'cash' ? currency.rateMicros : 1_000_000,
        decimals: method === 'cash' ? currency.decimals : 2,
      },
    ]);
  };

  const finish = () => {
    if (!current.settled) return;
    onDone(taken, current.change, current.rounding);
  };

  /*
   * What is left to pay depends on how it is being paid: cash rounds to the
   * nearest ten agorot, a card does not. Ask settle() both ways.
   */
  const dueBy = (how: PaymentMethod) =>
    settle(
      total,
      [...taken, { id: 'probe', method: how, currency: 'ILS', amount: 0, rateMicros: 1_000_000, decimals: 2 }],
      { cashStepAgorot: settings.cashStepAgorot },
    ).remaining;

  /** The rest of the bill, in one method, and the sale is over. */
  const payWholeBill = (how: PaymentMethod) => {
    const payment: TakenPayment = {
      id: newId(),
      method: how,
      currency: 'ILS',
      amount: dueBy(how),
      reference: how === 'tab' ? customer?.name : undefined,
      rateMicros: 1_000_000,
      decimals: 2,
    };
    const list = [...taken, payment];
    const st = settle(total, list, { cashStepAgorot: settings.cashStepAgorot });
    onDone(list, st.change, st.rounding);
  };

  /** They handed over a note. Take it, and see what is left. */
  const takeNote = (n: number) => {
    setError('');
    setTaken((list) => [
      ...list,
      { id: newId(), method: 'cash', currency: 'ILS', amount: n, rateMicros: 1_000_000, decimals: 2 },
    ]);
  };

  const methods: { key: PaymentMethod; label: string }[] = [
    { key: 'cash', label: t('pay.cash') },
    { key: 'card', label: t('pay.card') },
    ...(allowCredit ? [{ key: 'credit' as PaymentMethod, label: t('pay.credit') }] : []),
    ...(allowGift ? [{ key: 'gift' as PaymentMethod, label: t('pay.gift') }] : []),
    ...(allowTab ? [{ key: 'tab' as PaymentMethod, label: t('pay.tab') }] : []),
  ];

  return (
    <Modal
      title={t('pay.title')}
      onClose={onCancel}
      footer={
        <>
          <Button onClick={onCancel}>{t('common.cancel')}</Button>
          <div className="spacer" />
          {/* A button that cannot be pressed yet is just something else to read. */}
          {current.settled || detail ? (
            <Button variant="primary" size="lg" onClick={finish} disabled={!current.settled}>
              {t('pay.confirm')}
            </Button>
          ) : null}
        </>
      }
    >
      <div className="pay-due">
        <div className="k">{t('pay.amountDue')}</div>
        <div className="v tnum">{money(current.payable)}</div>
        {current.rounding !== 0 ? (
          <div className="xsmall muted">
            {t('pay.rounding')} {money(current.rounding)} · {t('pay.roundingNote')}
          </div>
        ) : null}
      </div>

      {taken.length > 0 ? (
        <div className="pay-list">
          {taken.map((p) => (
            <div className="pay-row" key={p.id}>
              <span className="strong">
                {paymentLabel(p.method, t, p.methodLabel)}
                {p.currency !== 'ILS' ? ` · ${p.currency}` : ''}
              </span>
              <span className="tnum">
                {p.currency === 'ILS' ? money(p.amount) : `${amount(p.amount, p.currency)} ${p.currency}`}
              </span>
              {p.currency !== 'ILS' ? (
                <span className="xsmall muted tnum">
                  = {money(current.paymentsIls.find((x) => x.id === p.id)?.ils ?? 0)}
                </span>
              ) : (
                <span />
              )}
              <Button size="sm" variant="ghost" aria-label={t('checkout.removeLine')} onClick={() => setTaken((l) => l.filter((x) => x.id !== p.id))}>
                ✕
              </Button>
            </div>
          ))}
        </div>
      ) : null}

      {current.settled ? (
        <div className="pay-change">
          <div className="k">{t('pay.changeDue')}</div>
          <div className="v tnum">{money(current.change)}</div>
        </div>
      ) : !detail ? (
        /* The sale as it almost always happens. */
        <div className="pay-quick">
          <Button variant="primary" size="lg" block onClick={() => payWholeBill('cash')}>
            {t('pay.payCash', { amount: money(dueBy('cash')) })}
          </Button>
          <Button size="lg" block onClick={() => payWholeBill('card')}>
            {t('pay.payCard', { amount: money(dueBy('card')) })}
          </Button>
          {allowTab ? (
            <Button
              size="lg"
              block
              onClick={() => (customer ? payWholeBill('tab') : onNeedCustomer?.())}
            >
              {customer
                ? t('pay.payTabNamed', { amount: money(dueBy('card')), name: customer.name })
                : t('pay.payTabWho')}
            </Button>
          ) : null}
          <div className="pay-notes-label">{t('pay.gaveMe')}</div>
          <div className="pay-notes">
            {[2000, 5000, 10_000, 20_000].map((n) => (
              <Button key={n} onClick={() => takeNote(n)}>
                {money(n)}
              </Button>
            ))}
          </div>
          <button type="button" className="disclose" onClick={() => setDetail(true)}>
            <span className="chev" aria-hidden>▸</span>
            {t('pay.otherWay')}
          </button>
        </div>
      ) : (
        <>
          <Field label={t('pay.method')}>
            <div className="btn-row">
              {methods.map((m) => (
                <Button
                  key={m.key}
                  variant={method === m.key ? 'primary' : 'default'}
                  onClick={() => {
                    setMethod(m.key);
                    setError('');
                  }}
                >
                  {m.label}
                </Button>
              ))}
            </div>
          </Field>

          {method === 'cash' && active.length > 1 ? (
            <Field label={t('common.currency')}>
              <div className="btn-row">
                {active.map((c) => (
                  <Button
                    key={c.code}
                    size="sm"
                    variant={currency.code === c.code ? 'primary' : 'default'}
                    onClick={() => setCurrency(c)}
                  >
                    {c.symbol} {c.code}
                  </Button>
                ))}
              </div>
            </Field>
          ) : null}

          {method === 'cash' && currency.code !== 'ILS' ? (
            <p className="small muted">
              {t('pay.amountIn', { currency: currency.code })}:{' '}
              <strong className="tnum">
                {amount(dueInCurrency(remainingIls, currency), currency.code)} {currency.code}
              </strong>{' '}
              <span className="xsmall">
                ({t('common.rate')} {(currency.rateMicros / 1_000_000).toFixed(3)})
              </span>
            </p>
          ) : null}

          {method === 'card' ? <p className="small muted">{t('pay.cardNote')}</p> : null}

          {method === 'tab' ? (
            <p className="small muted">
              {customer ? t('pay.tabNote', { name: customer.name }) : t('pay.tabNeedsCustomer')}
            </p>
          ) : null}

          {isVoucher ? (
            <Field
              label={t(method === 'gift' ? 'pay.giftCode' : 'pay.creditCode')}
              hint={
                Number.isFinite(creditInfo?.remaining)
                  ? t(method === 'gift' ? 'pay.giftBalance' : 'pay.creditBalance', {
                      amount: money(creditInfo!.remaining),
                    })
                  : undefined
              }
            >
              <div className="row">
                <input
                  value={creditCode}
                  onChange={(e) => setCreditCode(e.target.value)}
                  onBlur={() => creditCode.trim() && void lookupCredit(creditCode)}
                  placeholder={method === 'gift' ? 'GC00000123' : 'SC00000123'}
                  data-scan-target="true"
                />
                <Button disabled={!creditCode.trim()} onClick={() => void lookupCredit(creditCode)}>
                  {t('common.search')}
                </Button>
              </div>
            </Field>
          ) : null}

          <Field label={method === 'cash' ? t('pay.tendered') : t('common.amount')}>
            <MoneyInput
              className="big"
              value={entry}
              onValue={setEntry}
              decimals={method === 'cash' ? currency.decimals : 2}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') void add();
              }}
            />
          </Field>

          {method === 'cash' && currency.code === 'ILS' ? (
            <div className="btn-row">
              {[2000, 5000, 10_000, 20_000].map((n) => (
                <Button key={n} size="sm" onClick={() => setEntry(n)}>
                  {money(n)}
                </Button>
              ))}
            </div>
          ) : null}

          {error ? <p style={{ color: 'var(--danger)' }} className="small">{error}</p> : null}
          {note && !error ? <p className="small muted">{note}</p> : null}

          <div className="btn-row" style={{ marginBlockStart: 'var(--s-4)' }}>
            <Button variant="primary" onClick={() => void add()} disabled={entry <= 0}>
              {taken.length ? t('pay.addPayment') : t('common.add')}
            </Button>
            {/* Not for a voucher: "the whole bill" is not a thing a card of a
                fixed value can do, and this shortcut carries no card number. */}
            {taken.length === 0 && !isVoucher ? <Button onClick={payAll}>{t('pay.exact')}</Button> : null}
            <div className="spacer" />
            <div className="small muted">
              {t('pay.stillDue')} <strong className="tnum">{money(remainingIls)}</strong>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}
